import React, {useState, useEffect, useRef, useImperativeHandle} from 'react';
import {createPortal} from 'react-dom';
import {Link} from 'react-router-dom';
import post from '../utils/post.js';
import formatMoney from '../utils/formatMoney.js';
import clipboard from '../utils/clipboard.js';
import convertDataString from '../utils/convertDataString.js';
import usePopupAnimation from '../utils/usePopupAnimation.js';
import UserEdit from './UserEdit.js';
import UserReservationsPopup from './UserReservationsPopup.js';
import UserSessionsPopup from './UserSessionsPopup.js';
import UserQR from '../pages/UserQR.js';
import ConfirmPopup from './ConfirmPopup.js';
import EditableProfilePhoto from './EditableProfilePhoto.js';
import FormTextArea from './FormTextArea.js';
import '../styles/user.scss';

const SUMMARY_FILTERS = {
	INSTRUCTOR: 'instructor',
	NOT_REGISTERED: 'not-registered',
	NOT_MEMBER: 'not-member',
};

// Sdílený obsah sekce Uživatelé (karty uživatelů) – používá ho stránka /user
// i popup v monitoringu (ikonka „pozval"). Hledaný výraz je řízený zvenku
// (stránka ho drží v horní liště, popup ho předvyplňuje jménem); klik na jméno
// v kartě ho mění přes onChangeSearch. Počet uživatelů a obnovení leží u kontejneru
// – počet chodí přes onInfoChange, obnovení přes ref (refresh). reloadKey vynutí
// nové načtení i beze změny hledání (stránka posílá klíč router location).
// onOpenLogs (popup v monitoringu) otevře logy v popupu místo prokliku do /userlog.
const UserCardList = React.forwardRef(({usrInfo, searchValue = '', onChangeSearch, reloadKey, onLoadingChange, onInfoChange, onOpenLogs}, ref) => {
	const popupAnimationMs = 300;
	const [loading, setLoading] = useState(false);
	const [data, setData] = useState([]);
	const [info, setInfo] = useState('');
	const [summary, setSummary] = useState({instructor: 0, notRegistered: 0, notMember: 0});
	const [activeSummaryFilter, setActiveSummaryFilter] = useState(null);
	const [showQR, setShowQR] = useState(false);
	const [qrInfo, setQrInfo] = useState({});
	const [dataDetail, setDataDetail] = useState({});
	const [showEdit, setShowEdit] = useState(false);
	const [offset, setOffset] = useState(0);
	const [loadedCnt, setLoadedCnt] = useState(0);
	const [showConfirmPopup, setShowConfirmPopup] = useState(false);
	const [bonusInfo, setBonusInfo] = useState({fromId: 0, parentId: 0, index: 0});
	const [showPushPopup, setShowPushPopup] = useState(false);
	const [pushUser, setPushUser] = useState({ID: 0, FIRSTNAME: '', LASTNAME: '', NICKNAME: ''});
	const [pushMessage, setPushMessage] = useState('');
	const [pushMessageError, setPushMessageError] = useState('');
	const [showReservationsPopup, setShowReservationsPopup] = useState(false);
	const [reservationsUser, setReservationsUser] = useState({ID: 0, FIRSTNAME: '', LASTNAME: '', NICKNAME: ''});
	const [showSessionsPopup, setShowSessionsPopup] = useState(false);
	const [sessionsUser, setSessionsUser] = useState({ID: 0, FIRSTNAME: '', LASTNAME: '', NICKNAME: ''});
	const {shouldRender: shouldRenderPushPopup, isClosing: isClosingPushPopup} = usePopupAnimation(showPushPopup, popupAnimationMs);
	const reloadKeyRef = useRef(reloadKey);

	useEffect(() => {
		if (onLoadingChange) {
			onLoadingChange(loading);
		}
	}, [loading, onLoadingChange]);

	useEffect(() => {
		if (onInfoChange) {
			onInfoChange(info);
		}
	}, [info, onInfoChange]);

	// Změna hledání → načteme od začátku se stávajícím filtrem karet. Nová location
	// (proklik odjinud, reloadKey) navíc filtr zruší – jako při prvním otevření.
	useEffect(() => {
		let summaryFilter = activeSummaryFilter;

		if (reloadKeyRef.current !== reloadKey) {
			reloadKeyRef.current = reloadKey;
			summaryFilter = null;
			setActiveSummaryFilter(null);
		}

		setOffset(0);
		getData(searchValue, 0, summaryFilter);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [searchValue, reloadKey]);

	const loadNext = () => {
		setOffset((prevOffset) => {
			let newOffset = prevOffset + usrInfo.LIST_ROWS;
			getData(searchValue, prevOffset + usrInfo.LIST_ROWS, activeSummaryFilter);
			return newOffset;
		});
	};

	const getData = (search, offset, summaryFilter = null) => {
		setLoading(true);

		post('getListUser', {search: search, offset: offset, summaryFilter: summaryFilter})
			.then((res) => {
				if (offset === 0) {
					setData(res.data.data);
				} else {
					setData((prevRows) => {
						const updatedRows = [...prevRows, ...res.data.data];
						return updatedRows;
					});
				}

				setLoadedCnt(res.data.data.length);

				setInfo(res.data.data_cnt);
				setSummary(res.data.summary || {instructor: 0, notRegistered: 0, notMember: 0});
				setLoading(false);
			})
			.catch((error) => {
				console.error(error);
				setLoading(false);
			});
	};

	// Klik na jméno v kartě (pozvaný, klient, kdo pozval) → vyhledat ho. Filtr karet
	// se ruší, hledá se konkrétní člověk. Stejné hledání by efekt nespustilo, proto
	// se v tom případě načítá rovnou.
	const searchUser = (value) => {
		setActiveSummaryFilter(null);

		if (value === searchValue) {
			setOffset(0);
			getData(value, 0, null);
			return;
		}

		onChangeSearch(value);
	};

	const saveMember = (index, id, val) => {
		setLoading(true);

		post('saveMember', {ID: id, CLUBMEMBER: val})
			.then((res) => {
				data[index].CLUBMEMBER = res.data;
				setLoading(false);
			})
			.catch((error) => {
				console.error(error);
				setLoading(false);
			});
	};

	const savePermanent = (index, id, val) => {
		setLoading(true);

		post('savePermanent', {ID: id, PERNAMENTMEMBER: val})
			.then((res) => {
				data[index].PERNAMENTMEMBER = res.data;
				setLoading(false);
			})
			.catch((error) => {
				console.error(error);
				setLoading(false);
			});
	};

	const saveActive = (index, id, val) => {
		if (id !== 1) {
			setLoading(true);

			post('saveActive', {ID: id, ACTIVE: val})
				.then((res) => {
					data[index].ACTIVE = res.data;
					setLoading(false);
				})
				.catch((error) => {
					console.error(error);
					setLoading(false);
				});
		}
	};

	const saveUnlock = (index, id, locked) => {
		if (locked === 1) {
			setLoading(true);

			post('saveUnlock', {ID: id})
				.then((res) => {
					data[index].LOCKED = 0;
					setLoading(false);
				})
				.catch((error) => {
					console.error(error);
					setLoading(false);
				});
		}
	};

	const logAsUser = (id) => {
		setLoading(true);

		post('logAsUser', {ID: id})
			.then((res) => {
				window.location.href = '/';
			})
			.catch((error) => {
				console.error(error);
				setLoading(false);
			});
	};

	const testEmail = (id) => {
		setLoading(true);

		post('testEmail', {ID: id})
			.then((res) => {
				setLoading(false);
			})
			.catch((error) => {
				console.error(error);
				setLoading(false);
			});
	};

	const editUser = (rec) => {
		setShowEdit(true);
		setDataDetail(rec);
	};

	const closeEdit = () => {
		setShowEdit(false);
	};

	const updateUserRow = (username, firstName, lastName, nickname, phone, prefix, id, message, payedtoDT, topayTotal, instructor, sysadmin, manager, entryPriceOne) => {
		data?.forEach((line, index) => {
			if (parseInt(line.ID) === parseInt(id)) {
				data[index].EMAIL = username;
				data[index].FIRSTNAME = firstName;
				data[index].LASTNAME = lastName;
				data[index].NICKNAME = nickname;
				data[index].PHONES = '+' + prefix + ' ' + phone[0] + phone[1] + phone[2] + ' ' + phone[3] + phone[4] + phone[5] + ' ' + phone[6] + phone[7] + phone[8];
				data[index].MESSAGE = message ? message.replace(/\n/g, '<br>') : '';
				data[index].TOPAY_TOTAL = topayTotal;
				data[index].PAYEDTO_DT = payedtoDT;
				data[index].INSTRUCTOR = instructor;
				data[index].SYSADMIN = sysadmin;
				data[index].MANAGER = manager;
				data[index].ENTRY_PRICE_ONE = entryPriceOne;

				if (convertDataString(payedtoDT) >= new Date().toISOString().slice(0, 10)) {
					data[index].PAYEDTO_DT_IN = 1;
				} else {
					data[index].PAYEDTO_DT_IN = 0;
				}

				setData([...data]);
			}
		});
	};

	const getQRData = (id) => {
		setLoading(true);

		post('getQRData', {ID: id})
			.then((res) => {
				setQrInfo(res.data);
				setShowQR(true);
				setLoading(false);
			})
			.catch((error) => {
				console.error(error);
				setLoading(false);
			});
	};

	const closeQR = () => {
		setShowQR(false);
	};

	const handleRefresh = () => {
		getData(searchValue, offset, activeSummaryFilter);
	};

	useImperativeHandle(ref, () => ({refresh: handleRefresh}));

	const toggleSummaryFilter = (summaryFilter) => {
		const nextSummaryFilter = activeSummaryFilter === summaryFilter ? null : summaryFilter;

		setActiveSummaryFilter(nextSummaryFilter);
		setOffset(0);
		getData(searchValue, 0, nextSummaryFilter);
	};

	const canManageUsers = usrInfo.sysAdmin === 1;
	const canEditUsers = usrInfo.sysAdmin === 1;
	const canViewMemberSearch = true;
	const canLogAsUser = true;
	const canViewQr = true;
	const canEditPhoto = true;
	const canAddBonus = true;
	const canSendPush = true;

	const openPushPopup = (user) => {
		setPushUser(user);
		setPushMessage('');
		setPushMessageError('');
		setShowPushPopup(true);
	};

	const closePushPopup = () => {
		setShowPushPopup(false);
		setPushMessage('');
		setPushMessageError('');
		setPushUser({ID: 0, FIRSTNAME: '', LASTNAME: '', NICKNAME: ''});
	};

	const openReservationsPopup = (user) => {
		setReservationsUser(user);
		setShowReservationsPopup(true);
	};

	const closeReservationsPopup = () => {
		setShowReservationsPopup(false);
		setReservationsUser({ID: 0, FIRSTNAME: '', LASTNAME: '', NICKNAME: ''});
	};

	const openSessionsPopup = (user) => {
		setSessionsUser(user);
		setShowSessionsPopup(true);
	};

	const closeSessionsPopup = () => {
		setShowSessionsPopup(false);
		setSessionsUser({ID: 0, FIRSTNAME: '', LASTNAME: '', NICKNAME: ''});
	};

	const sendUserPush = (e) => {
		e.preventDefault();

		if (pushMessage.trim() === '') {
			setPushMessageError('povinné');
			return;
		}

		setPushMessageError('');
		setLoading(true);

		post('sendUserPushNotification', {USRID: pushUser.ID, MESSAGE: pushMessage.trim()})
			.then(() => {
				setLoading(false);
				closePushPopup();
			})
			.catch((error) => {
				console.error(error);
				setLoading(false);
				setPushMessageError(error?.response?.data?.error || 'Nepodařilo se odeslat push notifikaci');
			});
	};

	const closeConfirmPopup = () => {
		setShowConfirmPopup(false);
	};

	const addBonus = (fromId, parentId) => {
		setBonusInfo({fromId: fromId, parentId: parentId});
		setShowConfirmPopup(true);
	};

	const confirmConfirmPopup = () => {
		setLoading(true);

		post('addBonus', {FROM_USRID: bonusInfo.fromId, PARENT_USRID: bonusInfo.parentId})
			.then((res) => {
				setShowConfirmPopup(false);
				setOffset(0);
				getData(searchValue, 0);
				setLoading(false);
			})
			.catch((error) => {
				console.error(error);
				setLoading(false);
			});
	};

	const getUserName = (line) => {
		if (line.NICKNAME !== '') {
			return `${line.LASTNAME} ${line.FIRSTNAME} (${line.NICKNAME})`;
		}

		return `${line.LASTNAME} ${line.FIRSTNAME}`;
	};

	const renderHeaderFlags = (line) => {
		const flags = [];

		if (line.SYSADMIN === 1) {
			flags.push(
				<span className="user-page__flag user-page__flag--danger" key="adm">
					ADM
				</span>,
			);
		}

		if (line.MANAGER === 1) {
			flags.push(
				<span className="user-page__flag user-page__flag--danger" key="man">
					MAN
				</span>,
			);
		}

		if (line.INSTRUCTOR === 1) {
			flags.push(
				<span className="user-page__flag user-page__flag--success" key="ins">
					INS
				</span>,
			);
		}

		if (parseInt(line.REGISTERED, 10) === 0) {
			flags.push(
				<span className="user-page__flag user-page__flag--registered" key="reg">
					NOT REG
				</span>,
			);
		}

		if (line.MESSAGE !== '') {
			flags.push(<div className="icon_message small" key="message" />);
		}

		if (Number(line.TOPAY_TOTAL) < 0) {
			flags.push(
				<span className="user-page__flag user-page__flag--success" key="overpay">
					{formatMoney(Math.abs(Number(line.TOPAY_TOTAL)))}
				</span>,
			);
		}

		if (Number(line.TOPAY_TOTAL) > 0) {
			flags.push(
				<span className="user-page__flag user-page__flag--danger" key="topay">
					{formatMoney(line.TOPAY_TOTAL)}
				</span>,
			);
		}

		return flags;
	};

	let list = [];
	const summaryCards = [
		{
			id: SUMMARY_FILTERS.INSTRUCTOR,
			label: 'INSTRUKTOŘI',
			value: summary.instructor,
			toneClassName: 'user-page__summary-card--success',
		},
		{
			id: SUMMARY_FILTERS.NOT_REGISTERED,
			label: 'NEREGISTROVANÍ',
			value: summary.notRegistered,
			toneClassName: 'user-page__summary-card--registered',
		},
		{
			id: SUMMARY_FILTERS.NOT_MEMBER,
			label: 'NEČLENOVÉ',
			value: summary.notMember,
			toneClassName: 'user-page__summary-card--warning',
		},
	];

	data?.forEach((line, index) => {
		const usrName = getUserName(line);

		let listPromoUsers = [];
		let listInstructorUsers = [];
		const lastLog = line.LAST_LOG || null;
		const lastLogIsSmartlock = parseInt(lastLog?.SMARTLOCK, 10) === 1;

		line.MY_PROMO_USERS?.forEach((line2, index2) => {
			let clr = 'green';
			if (parseInt(line2.CLUBMEMBER) === 0) {
				clr = 'gray';
			}
			listPromoUsers.push(
				<div className={`list_new ${clr} user-page__nested-card`} key={`PROMO_USER${index2}`}>
					<div className="list_new_head txt_l">
						<span className="fnt_white lnk2 fl_l mt_03 cr_p user-page__promo-user-name" onClick={() => searchUser(`${line2.LASTNAME} ${line2.FIRSTNAME}`)}>
							{line2.LASTNAME} {line2.FIRSTNAME} {line2.NICKNAME !== '' ? '(' + line2.NICKNAME + ')' : ''}
						</span>
						{!canAddBonus ? null : line2.CLUBMEMBER === 0 ? (
							<button className={`button check2 fl_r cr_d`}>+{usrInfo.PROMO_BONUS}</button>
						) : (
							<button className={`button check2 green fl_r`} onClick={() => addBonus(line2.ID, line.ID)}>
								+{usrInfo.PROMO_BONUS}
							</button>
						)}
						{line2.PERNAMENTMEMBER === 1 ? (
							<>
								<img className={`icon2 permanent active fl_r mr_2 mt_02 user-page__promo-user-permanent`} alt="" />
								<span className="fl_r mr_1 mt_03 fnt_12">{line2.PAYEDTO_DT}</span>
							</>
						) : (
							''
						)}
						<div className="zero h0" />
					</div>
				</div>,
			);
		});

		line.INSTRUCTOR_USERS?.forEach((line2, index2) => {
			let clr = 'green';
			if (parseInt(line2.CLUBMEMBER, 10) === 0) {
				clr = 'gray';
			}

			listInstructorUsers.push(
				<div className={`list_new ${clr} user-page__nested-card user-page__nested-card--instructor`} key={`INSTRUCTOR_USER${index2}`}>
					<div className="list_new_head txt_l">
						<span className="fnt_white lnk2 fl_l mt_03 cr_p user-page__promo-user-name" onClick={() => searchUser(`${line2.LASTNAME} ${line2.FIRSTNAME}`)}>
							{line2.LASTNAME} {line2.FIRSTNAME} {line2.NICKNAME !== '' ? '(' + line2.NICKNAME + ')' : ''}
						</span>
						<img className={`icon2 permanent ${parseInt(line2.PERNAMENTMEMBER, 10) === 1 ? 'active' : ''} fl_r mr_2 mt_02 user-page__promo-user-permanent`} alt="" />
						{line2.PERNAMENTMEMBER === 1 ? <span className="fl_r mr_1 mt_03 fnt_12">{line2.PAYEDTO_DT}</span> : ''}
						<div className="zero h0" />
					</div>
				</div>,
			);
		});

		let listNotPayedAddPerson = [];
		let totalNotPayedAddPerson = 0;
		let totalNotPayerAddPersonPrices = 0;
		const recentReservations = line.RESERVATIONS || [];
		line.not_payed_add_person.forEach((line2, index2) => {
			let clr = '';
			const rowClassName = parseInt(line2.TYPE) === 2 ? 'green' : 'orange';
			const userAmount = parseInt(line2.TYPE) === 1 && parseInt(line2.PAYED) === 0 ? formatMoney(line2.NOT_PAYED_PRICES_USR) : formatMoney(0);
			const companionAmount = line2.ADD_PERSON > 0 && parseInt(line2.ADD_PERSON_PAYED) === 0 ? formatMoney(line2.NOT_PAYED_PRICES) : formatMoney(0);
			if (parseInt(line2.IS_FUTURE) === 1) {
				clr = 'fnt_yellow';
			}
			listNotPayedAddPerson.push(
				<div className={`list_new ${rowClassName} user-page__nested-card`} key={`NOT_PAYED_ADD_PERSON${index2}`}>
					<div className="list_new_head txt_l user-page__unpaid-row">
						<span className={`user-page__unpaid-title ${clr}`}>
							{line2.RESERVATIONDTS} - {line2.RESERVATIONLNG_H}h{line2.ADD_PERSON > 0 ? ` +${line2.ADD_PERSON}x` : ''}
						</span>
						<div className="user-page__unpaid-values">
							<span className="user-page__unpaid-value">{userAmount}</span>
							<span className="user-page__unpaid-value user-page__unpaid-value--accent">{companionAmount}</span>
						</div>
					</div>
				</div>,
			);

			if (parseInt(line2.ADD_PERSON_PAYED) === 0) {
				totalNotPayedAddPerson += parseInt(line2.ADD_PERSON);
				totalNotPayerAddPersonPrices += parseFloat(line2.NOT_PAYED_PRICES);
			}

			if (parseInt(line2.PAYED) === 0) {
				++totalNotPayedAddPerson;
				totalNotPayerAddPersonPrices += parseFloat(line2.NOT_PAYED_PRICES_USR);
			}
		});

		const displayName = `${line.FIRSTNAME} ${line.LASTNAME}`.trim() || line.usrName || '';

		list.push(
			<div className={`user-page__card${parseInt(line.ACTIVE) === 0 ? ' is-inactive' : ''}`} key={index}>
				<div className="user-page__card-head">
					<div className="user-page__identity">
						<div className="user-page__avatar-wrap">
							<EditableProfilePhoto photoUrl={line.PROFILE_PHOTO_URL} displayName={displayName} canEditPhoto={canEditPhoto} uploadUrl="/rest/uploadInstructorProfilePhoto" deleteUrl="/rest/deleteInstructorProfilePhoto" entityKey={line.ID} avatarWidth="6rem" avatarHeight="6rem" />
							<div className={`user-page__paidto${parseInt(line.PAYEDTO_DT_IN) === 1 ? ' is-valid' : ''}`}>{line.PAYEDTO_DT}</div>
						</div>

						<div className="user-page__profile">
							<button type="button" className="user-page__name" onClick={() => clipboard(usrName)}>
								{usrName}
							</button>
							<a className="user-page__link" href={`tel:${line.PHONES}`}>
								{line.PHONES}
							</a>
							<a className="user-page__link user-page__link--email" href={`mailto:${line.EMAIL}`}>
								{line.EMAIL}
							</a>
							<div className="user-page__meta">
								<span className={parseInt(line.LASTACTIVITY_TODAY) === 1 ? 'user-page__lastactivity is-today' : 'user-page__lastactivity'}>{line.LASTACTIVITYDTS}</span>
								<button type="button" className="user-page__session-button" onClick={() => openSessionsPopup(line)} title="Zobrazit sessions" aria-label={`Zobrazit sessions uživatele ${usrName}`}>
									<img className="icon2 key active" alt="" />
									<span>{line.SESSION_CNT}x</span>
								</button>
							</div>
							<div className="user-page__meta-actions">
								{canManageUsers && parseInt(usrInfo.usrId) === 1 && line.ID === 1 ? (
									<button type="button" className="user-page__meta-action" onClick={() => testEmail(line.ID)}>
										Test email
									</button>
								) : null}
								{canSendPush && parseInt(line.HAS_PUSH_SUBSCRIPTION) === 1 ? (
									<button type="button" className="user-page__meta-action" onClick={() => openPushPopup(line)}>
										Push notify
									</button>
								) : null}
							</div>
						</div>
					</div>

					<div className="user-page__head-side">
						<div className="user-page__head-top">
							<div className="user-page__flag-row">{renderHeaderFlags(line)}</div>
							<button type="button" className={`user-page__id-chip${parseInt(line.ACTIVE) === 1 ? ' is-active' : ''}`} onClick={() => (canManageUsers ? saveActive(index, line.ID, line.ACTIVE) : null)}>
								{line.ID}
							</button>
						</div>

						{canManageUsers ? (
							<div className="user-page__action-grid">
								<button type="button" className="user-page__icon-button" onClick={() => saveMember(index, line.ID, line.CLUBMEMBER)}>
									<img className={`icon2 member ${parseInt(line.CLUBMEMBER) === 1 ? 'active' : ''}`} alt="" />
								</button>
								<button type="button" className="user-page__icon-button" onClick={() => savePermanent(index, line.ID, line.PERNAMENTMEMBER)}>
									<img className={`icon2 permanent ${parseInt(line.PERNAMENTMEMBER) === 1 ? 'active' : ''}`} alt="" />
								</button>
								<button type="button" className={`user-page__icon-button${parseInt(line.LOCKED) === 1 ? ' is-attention' : ''}`} onClick={() => saveUnlock(index, line.ID, line.LOCKED)}>
									<img className={`icon2 lock ${parseInt(line.LOCKED) === 1 ? 'active' : ''}`} alt="" />
								</button>
							</div>
						) : null}

						<div className="user-page__action-grid">
							{canAddBonus && line.PARENT_PROMO_CODE !== '' ? (
								<button type="button" className="user-page__icon-button" onClick={() => searchUser(`${line.PARENT_PROMO_CODE_LASTNAME} ${line.PARENT_PROMO_CODE_FIRSTNAME}`)}>
									<img className="icon2 bonus" alt="" />
								</button>
							) : null}
							{parseInt(usrInfo.usrId) === 1 ? (
								onOpenLogs ? (
									<button type="button" className="user-page__icon-button" onClick={() => onOpenLogs(line)} title="Zobrazit logy" aria-label={`Zobrazit logy uživatele ${usrName}`}>
										<img className="icon2 userlog" alt="" />
									</button>
								) : (
									<Link className="user-page__icon-button" to={`/userlog/${line.EMAIL}`}>
										<img className="icon2 userlog" alt="" />
									</Link>
								)
							) : null}
							{canViewMemberSearch && line.ID !== 1 ? (
								<Link className="user-page__icon-button" to={`/member/${line.LASTNAME} ${line.FIRSTNAME}`}>
									<img className="icon2 member" alt="" />
								</Link>
							) : null}
							{canLogAsUser && line.ID !== 1 ? (
								<button type="button" className="user-page__icon-button" onClick={() => logAsUser(line.ID)}>
									<img className="icon2 logas" alt="" />
								</button>
							) : null}
							{canEditUsers ? (
								<button type="button" className="user-page__icon-button" onClick={() => editUser(line)}>
									<img className="icon2 edit" alt="" />
								</button>
							) : null}
							{canViewQr ? (
								<button type="button" className="user-page__icon-button" onClick={() => getQRData(line.ID)}>
									<img alt="" className="icon2 qr" />
								</button>
							) : null}
						</div>
					</div>
				</div>

				<div className="user-page__detail-grid">
					<div className="user-page__detail-card">
						<div className="user-page__detail-card-head user-page__detail-card-head--with-action">
							<div className="user-page__detail-card-title">Vstupy</div>
							<div className="user-page__detail-card-actions">
								<span className="user-page__detail-card-value">{line.USR_RECORDS}x</span>
								<button type="button" className="user-page__detail-card-action" onClick={() => openReservationsPopup(line)} title="Zobrazit rezervace" aria-label={`Zobrazit rezervace uživatele ${usrName}`}>
									i
								</button>
							</div>
						</div>
						<div className="user-page__detail-card-body user-page__detail-card-body--compact">
							{recentReservations.length > 0 ? (
								recentReservations.map((reservation, reservationIndex) => (
									<div className={`user-page__detail-row user-page__detail-row--compact${parseInt(reservation.IS_FUTURE) === 1 ? ' is-future' : ''}`} key={`RESERVATION_${line.ID}_${reservationIndex}`}>
										<span className="user-page__detail-row-title">{reservation.RESERVATIONDT}</span>
										<span className="user-page__detail-row-value">
											{reservation.RESERVATIONLNG_H}h{reservation.ADD_PERSON > 0 ? ` +${reservation.ADD_PERSON}x` : ''}
										</span>
									</div>
								))
							) : (
								<div className="user-page__detail-note">Bez posledních vstupů.</div>
							)}
						</div>
					</div>

					<div className="user-page__detail-card user-page__detail-card--danger user-page__detail-card--unpaid">
						<div className="user-page__detail-card-head">
							<div className="user-page__detail-card-title">Nezapl. vstupy {line.ENTRY_PRICE_ONE > -1 && line.ENTRY_PRICE_ONE !== '' ? `(${formatMoney(line.ENTRY_PRICE_ONE)})` : null}</div>
							<div className="user-page__detail-card-value">
								{formatMoney(totalNotPayerAddPersonPrices)} ({totalNotPayedAddPerson}x)
							</div>
						</div>
						<div className="user-page__detail-card-body">
							<div className="user-page__nested-list">{listNotPayedAddPerson}</div>
							{data.NOT_PAYED_ADD_PERSON_DETAIL !== '' ? <div className="user-page__detail-note" dangerouslySetInnerHTML={{__html: data.NOT_PAYED_ADD_PERSON_DETAIL}}></div> : null}
						</div>
					</div>

					{lastLog ? (
						<div className="user-page__detail-card user-page__detail-card--full">
							<div className="user-page__detail-card-body">
								<div className="user-page__last-log">
									<span className="user-page__last-log-date">{lastLog.CRDTS}</span>
									<span className={`user-page__last-log-action${lastLogIsSmartlock ? ' user-page__last-log-action--smartlock' : ''}`}>{lastLog.ACTION}</span>
								</div>
							</div>
						</div>
					) : null}

					<div className="user-page__detail-card user-page__detail-card--promo user-page__detail-card--full">
						<div className="user-page__detail-card-body">
							<div className="user-page__promo-code">
								<span>Pozvaní</span>
								<button type="button" className="user-page__inline-link" onClick={() => clipboard(line.MY_PROMO_CODE)}>
									{line.MY_PROMO_CODE}
								</button>
							</div>
							{listPromoUsers.length > 0 ? <div className="user-page__nested-list user-page__nested-list--scrollable">{listPromoUsers}</div> : null}
						</div>
					</div>

					{parseInt(line.INSTRUCTOR, 10) === 1 ? (
						<div className="user-page__detail-card user-page__detail-card--promo user-page__detail-card--full">
							<div className="user-page__detail-card-body">
								<div className="user-page__promo-code">
									<span>Klienti</span>
								</div>
								{listInstructorUsers.length > 0 ? <div className="user-page__nested-list user-page__nested-list--scrollable">{listInstructorUsers}</div> : ''}
							</div>
						</div>
					) : null}

					{line.MESSAGE !== '' ? (
						<div className="user-page__detail-card user-page__detail-card--full user-page__detail-card--muted">
							<div className="user-page__detail-card-head">
								<div className="user-page__detail-card-title">Zpráva</div>
							</div>
							<div className="user-page__detail-card-body">
								<div className="user-page__message" dangerouslySetInnerHTML={{__html: line.MESSAGE}}></div>
							</div>
						</div>
					) : null}
				</div>
			</div>,
		);
	});

	return (
		<>
			{/* Vnořené popupy jdou přes portál do body – v popupu z monitoringu by je jinak
			    rodičovský popup (backdrop-filter, animace) uzavřel do svého obdélníku. */}
			{createPortal(
				<>
					<ConfirmPopup showConfirmPopup={showConfirmPopup} showButtonConfirm cofirmConfirmPopup={confirmConfirmPopup} closeConfirmPopup={closeConfirmPopup} infoHeader="Přidání bonusu" infoMain="Opravdu chceš přidat bonus?" />
					{canEditUsers ? <UserEdit showEdit={showEdit} closeEdit={closeEdit} updateUserRow={updateUserRow} data={dataDetail} usrInfo={usrInfo} /> : null}
					<UserReservationsPopup showReservations={showReservationsPopup} closeReservations={closeReservationsPopup} user={reservationsUser} listRows={usrInfo.LIST_ROWS} />
					<UserSessionsPopup showSessions={showSessionsPopup} closeSessions={closeSessionsPopup} user={sessionsUser} />
					{canViewQr ? <UserQR showQR={showQR} closeQR={closeQR} qrInfo={qrInfo} /> : null}

					{canSendPush && shouldRenderPushPopup ? (
						<div className={`popup${isClosingPushPopup ? ' popup--closing' : ''}`}>
							<div className={`popup_content${isClosingPushPopup ? ' popup_content--closing' : ''}`}>
								<form method="POST" onSubmit={sendUserPush}>
									<div className="popup_content_in_head">
										<div className="popup_content_in_head_in">Push notifikace</div>
										<img
											alt=""
											className="popup_content_in_head_in-close"
											onClick={() => {
												closePushPopup();
											}}
										/>
									</div>
									<div className="popup_content_in_body">
										<div className="popup_content_in_body_in">
											<div className="zero h05" />
											<div className="txt_l fnt_b">
												{pushUser.LASTNAME} {pushUser.FIRSTNAME} {pushUser.NICKNAME !== '' ? `(${pushUser.NICKNAME})` : ''}
											</div>
											<div className="zero h2" />
											<FormTextArea label="Zpráva" value={pushMessage} setValue={setPushMessage} error={pushMessageError} height="11rem" />
										</div>
									</div>
									<div className="popup_content_in_foot">
										<div className="popup_content_in_foot_in">
											<input type="submit" name="button_send_push" value="Odeslat" className="button green mr_2" disabled={loading} />
											<button
												type="button"
												className="button"
												onClick={() => {
													closePushPopup();
												}}
											>
												Zpět
											</button>
										</div>
									</div>
								</form>
							</div>
						</div>
					) : (
						''
					)}
				</>,
				document.body,
			)}

			<div className="user-page__summary-panel">
				<div className="user-page__summary-grid">
					{summaryCards.map((card) => {
						const isActive = activeSummaryFilter === card.id;

						return (
							<button className={`home-summary-card user-page__summary-card ${card.toneClassName}${isActive ? ' is-active' : ''}`} key={card.id} type="button" aria-pressed={isActive} onClick={() => toggleSummaryFilter(card.id)}>
								<div className="home-summary-card__content">
									<span className="home-summary-card__eyebrow">{card.label}</span>
									<span className="home-summary-card__value">{card.value}</span>
								</div>
							</button>
						);
					})}
				</div>
			</div>

			<div className="user-page__content">
				{list}

				{parseInt(loadedCnt) === parseInt(usrInfo.LIST_ROWS) ? (
					<button className="button small loadNext user-page__load-button" onClick={() => loadNext()}>
						Načíst další
					</button>
				) : (
					<span className="user-page__load-note">Byly načteny všechny záznamy</span>
				)}
			</div>
		</>
	);
});

export default UserCardList;
